/**
 * Phones and tablets (docs/design/visual-overhaul.md §12): which layout the
 * window is in, which pointer it has, and the visual viewport the on-screen
 * keyboard leaves. One source for the app and the Console.
 *
 *   phone    narrower than 640 CSS px, or shorter than 500 in landscape
 *            (narrower than 1024, or with a coarse pointer)
 *   tablet   640 to 1023 wide; `nav` is "drawer" below 900 (portrait), the
 *            rail and sidebar become a left drawer
 *   desktop  1024 and wider: the desktop design, unchanged
 *
 * Touch sizing follows the pointer, never the width: `pointer` is "coarse"
 * when (pointer: coarse) matches (CSS uses the media query itself).
 *
 * `watchLayout()` writes `data-layout`, `data-nav` and `data-pointer` on
 * <html> (and on any extra elements it is given: the client's #nx-app), and
 * the visual viewport as CSS variables on <html>:
 *   --vv-h    the visual viewport's height (what the keyboard leaves)
 *   --vv-top  how far iOS scrolled the layout viewport to show a field
 *   --kb-h    the on-screen keyboard's height (0 when it is closed)
 * Chrome on Android resizes the layout viewport itself
 * (interactive-widget=resizes-content in index.html); iOS Safari does not,
 * so fixed layers that hold a field use `top: var(--vv-top); height: var(--vv-h)`.
 */
import { useSyncExternalStore } from 'react';

export type Layout = 'phone' | 'tablet' | 'desktop';
export type PointerKind = 'coarse' | 'fine';
export type NavMode = 'drawer' | 'docked';

export interface LayoutState {
  layout: Layout;
  pointer: PointerKind;
  /** Tablet: the rail and sidebar are a left drawer (portrait, under 900) or docked. */
  nav: NavMode;
  width: number;
  height: number;
}

export const PHONE_MAX_W = 640;
export const PHONE_LANDSCAPE_MAX_H = 500;
export const DESKTOP_MIN_W = 1024;
export const TABLET_DRAWER_MAX_W = 900;

/** The layout for a window of `w` x `h` CSS px. */
export function layoutFor(w: number, h: number, coarse: boolean): Layout {
  if (w < PHONE_MAX_W) return 'phone';
  // a phone on its side: short and wide (844x390, 932x430)
  if (h < PHONE_LANDSCAPE_MAX_H && w > h && (w < DESKTOP_MIN_W || coarse)) return 'phone';
  if (w < DESKTOP_MIN_W) return 'tablet';
  return 'desktop';
}

const mq = (q: string): boolean =>
  typeof matchMedia === 'function' ? matchMedia(q).matches : false;

function compute(): LayoutState {
  const w = typeof innerWidth === 'number' ? innerWidth : 1280;
  const h = typeof innerHeight === 'number' ? innerHeight : 800;
  const coarse = mq('(pointer: coarse)');
  const layout = layoutFor(w, h, coarse);
  return {
    layout,
    pointer: coarse ? 'coarse' : 'fine',
    nav: layout === 'tablet' && w < TABLET_DRAWER_MAX_W ? 'drawer' : 'docked',
    width: w,
    height: h,
  };
}

let state: LayoutState = compute();
const subs = new Set<() => void>();

/** The layout now (a stable object until something in it changes). */
export function readLayout(): LayoutState {
  return state;
}

/** Calls `fn` whenever the layout, pointer or nav mode changes; returns the stop. */
export function onLayout(fn: () => void): () => void {
  subs.add(fn);
  return () => subs.delete(fn);
}

/** True on a phone, or a tablet whose rail and sidebar are a drawer: one pane is shown at a time. */
export function isCompact(s: LayoutState = state): boolean {
  return s.layout === 'phone' || (s.layout === 'tablet' && s.nav === 'drawer');
}

/** The layout as a React value (re-renders when it changes). */
export function useLayout(): LayoutState {
  return useSyncExternalStore(onLayout, readLayout, readLayout);
}

const targets = new Set<HTMLElement>();

function paint(): void {
  if (typeof document === 'undefined') return;
  for (const el of [document.documentElement, ...targets]) {
    el.dataset.layout = state.layout;
    el.dataset.pointer = state.pointer;
    el.dataset.nav = state.nav;
  }
}

function paintViewport(): void {
  if (typeof document === 'undefined') return;
  const root = document.documentElement.style;
  const vv = (globalThis as { visualViewport?: VisualViewport }).visualViewport;
  if (!vv || state.layout === 'desktop') {
    // desktop: nothing moves (the frame keeps `inset: 0`)
    root.removeProperty('--vv-h');
    root.removeProperty('--vv-top');
    root.removeProperty('--kb-h');
    return;
  }
  const h = Math.round(vv.height);
  const top = Math.max(0, Math.round(vv.offsetTop));
  root.setProperty('--vv-h', h + 'px');
  root.setProperty('--vv-top', top + 'px');
  root.setProperty(
    '--kb-h',
    Math.max(0, Math.round(innerHeight - vv.height - vv.offsetTop)) + 'px',
  );
}

function update(): void {
  const next = compute();
  const changed =
    next.layout !== state.layout || next.pointer !== state.pointer || next.nav !== state.nav;
  // width and height ride along, but only a change of mode re-renders
  state = changed ? next : Object.assign(state, { width: next.width, height: next.height });
  if (changed) {
    paint();
    for (const fn of subs) fn();
  }
  paintViewport();
}

let watching = false;

/**
 * Starts writing the layout on <html> (and on `extra`, while it is given) and
 * follows the window, the pointer and the visual viewport. Returns the stop
 * for `extra`; the <html> watch stays for the page's life.
 */
export function watchLayout(extra?: HTMLElement | null): () => void {
  if (typeof window === 'undefined') return () => {};
  if (extra) targets.add(extra);
  if (!watching) {
    watching = true;
    addEventListener('resize', update);
    addEventListener('orientationchange', update);
    for (const q of ['(pointer: coarse)', '(orientation: portrait)'])
      globalThis.matchMedia?.(q).addEventListener?.('change', update);
    const vv = (globalThis as { visualViewport?: VisualViewport }).visualViewport;
    vv?.addEventListener('resize', paintViewport);
    vv?.addEventListener('scroll', paintViewport);
    state = compute();
  }
  paint();
  paintViewport();
  return () => {
    if (!extra) return;
    targets.delete(extra);
    for (const k of ['layout', 'pointer', 'nav'] as const) delete extra.dataset[k];
  };
}
